/**
 * A bike and its movement rules.
 * Ported from `Moto` (`Moto.h`), `bougeMoto` (`Jeu.c:234`) and the turning
 * geometry of `JeuActionClavier` (`Jeu.c:159`).
 */

import { makeSegment, type Segment } from "./trail";
import type { PlayerColor, TurnInput } from "./types";
import { Direction } from "./types";

export interface Bike {
  /** Top-left corner, floating point. */
  x: number;
  y: number;
  /** Position at the previous tick, for render interpolation. */
  px: number;
  py: number;
  /** Footprint, integer: 5x10 portrait, 10x5 landscape. */
  w: number;
  h: number;
  /** Speed in px/tick. */
  speed: number;
  dir: Direction;
  color: PlayerColor;
  /** Turned during the current tick (ex-`boolTourne`) — used by DOUBT resolution. */
  turnedThisTick: boolean;
  /** Last segment laid down (ex-`dernierMur`) — used by the high-speed graze test. */
  lastSegment: Segment | null;
}

export function makeBike(
  x: number,
  y: number,
  w: number,
  h: number,
  speed: number,
  dir: Direction,
  color: PlayerColor,
): Bike {
  return {
    x,
    y,
    px: x,
    py: y,
    w,
    h,
    speed,
    dir,
    color,
    turnedThisTick: false,
    lastSegment: null,
  };
}

/** Bounding box of the bike: [x, y, x+w, y+h]. */
export function bikeBox(b: Bike): [number, number, number, number] {
  return [b.x, b.y, b.x + b.w, b.y + b.h];
}

/**
 * Advances the bike by one tick and returns the trail segment left behind it.
 * Also applies the constant acceleration (`speed += acceleration`).
 */
export function stepBike(b: Bike, acceleration: number, trailLife: number): Segment {
  const thickness = Math.floor(b.speed) + 1;
  let seg: Segment;

  switch (b.dir) {
    case Direction.Up:
      seg = makeSegment(b.x, b.y + b.h - b.speed - 1, b.w, thickness, b.color, trailLife);
      b.y -= b.speed;
      break;
    case Direction.Down:
      seg = makeSegment(b.x, b.y, b.w, thickness, b.color, trailLife);
      b.y += b.speed;
      break;
    case Direction.Left:
      seg = makeSegment(b.x + b.w - b.speed - 1, b.y, thickness, b.h, b.color, trailLife);
      b.x -= b.speed;
      break;
    case Direction.Right:
      seg = makeSegment(b.x, b.y, thickness, b.h, b.color, trailLife);
      b.x += b.speed;
      break;
    default:
      seg = makeSegment(b.x, b.y, b.w, b.h, b.color, trailLife);
      break;
  }

  b.speed += acceleration;
  return seg;
}

/** Turn table: current direction -> requested absolute direction -> resulting direction. */
function resolveTurn(current: Direction, requested: TurnInput): Direction | null {
  switch (current) {
    case Direction.Up:
    case Direction.Down:
      return requested === Direction.Left || requested === Direction.Right ? requested : null;
    case Direction.Left:
    case Direction.Right:
      return requested === Direction.Up || requested === Direction.Down ? requested : null;
    default:
      return null;
  }
}

/**
 * Applies a turn (input = absolute direction, filtered to perpendiculars).
 * Returns the corner segment created, or `null` if the turn is rejected
 * (U-turn, same direction, undirected bike).
 *
 * Faithfully reproduces the repositioning of `JeuActionClavier`: on a turn the
 * bike pivots around a corner, its w/h are swapped, and a corner segment sized
 * with the pre-swap dimensions is dropped.
 */
export function turnBike(b: Bike, requested: TurnInput, trailLife: number): Segment | null {
  const next = resolveTurn(b.dir, requested);
  if (next === null) return null;

  const corner = makeSegment(b.x, b.y, b.w, b.h, b.color, trailLife);
  const dw = b.w - b.h;
  const dh = b.h - b.w;

  switch (b.dir) {
    case Direction.Up:
      if (next === Direction.Left) b.x += dw;
      break;
    case Direction.Down:
      if (next === Direction.Left) {
        b.x += dw;
        b.y += dh;
      } else {
        b.y += dh;
      }
      break;
    case Direction.Left:
      if (next === Direction.Up) b.y += dh;
      break;
    case Direction.Right:
      if (next === Direction.Up) {
        b.x += dw;
        b.y += dh;
      } else {
        b.x += dw;
      }
      break;
    default:
      break;
  }

  b.dir = next;
  const tmp = b.w;
  b.w = b.h;
  b.h = tmp;
  b.turnedThisTick = true;
  b.lastSegment = corner;
  return corner;
}
